import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { task, didCancel } from 'ember-concurrency'
import { Changeset } from 'ember-changeset'
import type { BufferedChangeset } from 'ember-changeset/types'
import type { IntlService } from 'ember-intl'
import { editStudentValidator } from 'teacher-ui-client/student/validator'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type ErrorHandlerService from 'teacher-ui-client/services/error-handler'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import type Student from 'teacher-ui-client/models/student'
import FormWrapperSubmitButton from 'teacher-ui-client/components/form-wrapper/submit-button'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { t } from 'ember-intl'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import { FormField } from 'teacher-ui-client/components/form-field'
import { or } from 'ember-truth-helpers'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import type Owner from '@ember/owner'

interface Args {
  student: Student
  usesExternalProvisioning?: boolean
  usesExternalAuthentication?: boolean
  usesExternalRostering?: boolean
}

interface Signature {
  Element: HTMLFormElement
  Args: Args
}

export class FormEditStudent extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service resourceConflict!: ResourceConflictService

  @service('error-handler') errorHandlerService!: ErrorHandlerService

  @service gradeSets!: GradeSetsService

  @tracked errorHandler!: ErrorHandler

  @tracked studentChangeset!: BufferedChangeset

  constructor(owner: Owner, args: Args) {
    super(owner, args)

    this.studentChangeset = Changeset(
      args.student,
      lookupValidator(editStudentValidator, { intl: this.intl }),
      editStudentValidator,
    )
    void this.studentChangeset.validate()

    this.errorHandler = this.errorHandlerService.create()

    this.errorHandler.registerErrorDefinition({
      statusCode: '422',
      serverMessage: 'login has already been taken',
      changesetProperty: 'login',
      changeset: this.studentChangeset,
      kind: 'changeset',
      translationKey: 'validation.message.teacher.loginTaken',
    })

    const conflictErrorDefinition = this.resourceConflict.errorHandlerDefinition()
    this.errorHandler.registerErrorDefinition(conflictErrorDefinition)
  }

  get gradeOptions() {
    return this.gradeSets.grades.map(({ position, name }: { position: number; name: string }) => ({
      text: name,
      value: position,
      uid: position,
    }))
  }

  saveStudent = task({ drop: true }, async (studentChangeset) => {
    await studentChangeset.save()
  })

  @action
  private rollback() {
    if (this.studentChangeset.isDirty && this.saveStudent.isIdle) {
      this.studentChangeset.rollback()
    }
  }

  @action
  onReset(event: Event) {
    event.preventDefault()
    this.rollback()
  }

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    const { errorHandler, studentChangeset } = this

    try {
      await this.saveStudent.perform(studentChangeset)

      const name = studentChangeset.get('fullName')
      const message = this.intl.t('manage.manageClasses.students.editStudent.success', { name })
      this.flashQueue.addSuccess({ subtitle: message })
    } catch (e: any) {
      if (!didCancel(e)) {
        const errors = e?.errors || []
        errorHandler.handle(errors)
        errorHandler.sendOtherErrorsToLog('Error while saving a student from the "edit-student" component')

        const student = studentChangeset.get('data')
        student.rollbackAttributes()

        const name = studentChangeset.get('fullName')
        const message = this.intl.t('manage.manageClasses.students.editStudent.error', { name })

        if (errorHandler.hasUnhandledErrors()) this.flashQueue.addFail({ subtitle: message })
      }
    }
  }

  <template>
    <form
      data-test-edit-student
      class="space-y-3"
      autocomplete="off"
      {{on "submit" this.onSubmit}}
      {{on "reset" this.onReset}}
      ...attributes
    >
      <div class="font-semibold">{{t "manage.manageClasses.students.editStudent.editDetails"}}</div>
      <div class="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-cols-2 lg:grid-rows-3">
        <FormField
          @changeset={{this.studentChangeset}}
          @propertyName="firstName"
          @labelText={{t "manage.firstName"}}
          as |field|
        >
          <field.label class="font-normal" />
          <field.input data-test-first-name disabled={{@usesExternalProvisioning}} />
        </FormField>
        <FormField
          @changeset={{this.studentChangeset}}
          @propertyName="lastName"
          @labelText={{t "manage.lastName"}}
          as |field|
        >
          <field.label class="font-normal" />
          <field.input data-test-last-name disabled={{@usesExternalProvisioning}} />
        </FormField>
        <FormField
          @changeset={{this.studentChangeset}}
          @propertyName="externalId"
          @labelText={{t "manage.manageClasses.students.editStudent.externalId"}}
          as |field|
        >
          <field.label class="font-normal" />
          <field.input data-test-external-id disabled={{@usesExternalProvisioning}} />
        </FormField>
        {{#unless @usesExternalAuthentication}}
          <FormField
            @changeset={{this.studentChangeset}}
            @propertyName="login"
            @labelText={{t "manage.login"}}
            as |field|
          >
            <field.label class="font-normal" />
            <field.input data-test-login />
          </FormField>
          <FormField
            @changeset={{this.studentChangeset}}
            @propertyName="plainPassword"
            @labelText={{t "manage.manageClasses.students.editStudent.password"}}
            as |field|
          >
            <field.label class="font-normal" />
            <field.input data-test-password />
          </FormField>
        {{/unless}}
        <FormField
          @changeset={{this.studentChangeset}}
          @propertyName="gradePosition"
          @labelText={{t "grade.label"}}
          as |field|
        >
          <field.label class="font-normal" />
          <field.select
            data-test-grade
            name="grade-number"
            @options={{this.gradeOptions}}
            @valueLookupKey="value"
            @defaultText={{t "manage.manageClasses.students.editStudent.gradePosition.defaultText"}}
            @disabled={{@usesExternalRostering}}
          />
        </FormField>
      </div>

      <div class="text-center">
        <UiButton
          data-test-edit-student-reset
          disabled={{this.studentChangeset.isPristine}}
          @appearance={{ButtonAppearance.muted}}
          type="reset"
        >
          {{t "manage.manageClasses.students.editStudent.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-edit-student-submit
          @disabled={{or this.studentChangeset.isPristine this.studentChangeset.isInvalid}}
          @appearance={{ButtonAppearance.regular}}
          @defaultText={{t "manage.manageClasses.students.editStudent.save"}}
          @state={{submitButtonState this.saveStudent.last this.errorHandler}}
        />
      </div>
    </form>
  </template>
}

export default FormEditStudent
